/**
 * Display-title rules for an editable user-authored skill (ENG-5396).
 *
 * The skill `name` is the stable identity: the `/slug`, the detail route, and
 * every reference. The display title is only the human-facing label in the
 * Skills catalog and the task picker. `publish_user_skill` stores a blank
 * title as the slug, so a blank or multi-line title has to be rejected here,
 * before the PUT, or the catalog label silently becomes the slug or a broken
 * row.
 */

/** ASCII controls and DEL. A display title is one catalog line. */
const CONTROL_OR_BREAK = /[\u0000-\u001F\u007F]/;

/** Inline error for a display title, or null when it can be saved. */
export function skillDisplayTitleError(title: string): string | null {
  if (title.trim() === "") return "Display title can't be empty.";
  if (CONTROL_OR_BREAK.test(title)) return "Display title must be a single line.";
  return null;
}

export type PreparedUserSkillEdit =
  | {
      ok: true;
      /** The slug the caller passed. Never derived from the title. */
      name: string;
      title: string;
      description: string;
      body: string;
    }
  | {
      ok: false;
      titleError: string | null;
      instructionsError: string | null;
    };

/**
 * Build the edit that `PUT /api/user-skills/{name}` may send.
 *
 * Returns `ok: false` (and no payload) when the display title or the
 * instructions are empty or invalid, so the caller does not submit. On
 * success the title is trimmed and `name` is the original slug.
 */
export function prepareUserSkillEdit(input: {
  name: string;
  title: string;
  description: string;
  body: string;
}): PreparedUserSkillEdit {
  const titleError = skillDisplayTitleError(input.title);
  const instructionsError = input.body.trim() ? null : "Instructions can't be empty.";
  if (titleError || instructionsError) {
    return { ok: false, titleError, instructionsError };
  }
  return {
    ok: true,
    name: input.name,
    title: input.title.trim(),
    description: input.description,
    body: input.body,
  };
}
